import React from 'react';
import { Plane, Calendar, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../common/Button';
import { useNavigate, useLocation } from 'react-router-dom';

interface ComingSoonPageProps {
  module?: 'travel' | 'calendar' | 'Emergency' | 'Calendar';
}

export function ComingSoonPage({ module: moduleProp }: ComingSoonPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get module from prop, location state, or default
  const module = moduleProp || (location.state as any)?.module || 'default';

  const moduleConfig: Record<string, any> = {
    travel: {
      title: 'Travel Management',
      description: 'We\'re working hard to bring you comprehensive travel planning and management features.',
      icon: <Plane size={64} className="text-blue-600" />,
      features: [
        'Trip planning and itineraries',
        'Accommodation bookings',
        'Transportation management',
        'Travel document storage',
        'Passport and visa tracking',
        'Booking confirmations'
      ]
    },
    calendar: {
      title: 'Calendar Management',
      description: 'We\'re developing powerful calendar features to help you stay organized and on schedule.',
      icon: <Calendar size={64} className="text-green-600" />,
      features: [
        'Event scheduling and management',
        'Calendar sharing and collaboration',
        'Reminder and notification system',
        'Integration with other modules',
        'Mobile calendar sync',
        'Custom calendar views'
      ]
    },
    Calendar: {
      title: 'Calendar Management',
      description: 'We\'re developing powerful calendar features to help you stay organized and on schedule.',
      icon: <Calendar size={64} className="text-green-600" />,
      features: [
        'Event scheduling and management',
        'Calendar sharing and collaboration',
        'Reminder and notification system',
        'Integration with other modules',
        'Mobile calendar sync',
        'Custom calendar views'
      ]
    },
    emergency: {
      title: 'Emergency Management',
      description: 'We\'re working on comprehensive emergency preparedness and management features.',
      icon: <AlertTriangle size={64} className="text-red-600" />,
      features: [
        'Emergency contact management',
        'Emergency plans and procedures',
        'Hazard preparedness guides',
        'Emergency kit tracking',
        'Drill scheduling and reminders',
        'Quick access emergency card'
      ]
    },
    Emergency: {
      title: 'Emergency Management',
      description: 'We\'re working on comprehensive emergency preparedness and management features.',
      icon: <AlertTriangle size={64} className="text-red-600" />,
      features: [
        'Emergency contact management',
        'Emergency plans and procedures',
        'Hazard preparedness guides',
        'Emergency kit tracking',
        'Drill scheduling and reminders',
        'Quick access emergency card'
      ]
    },
    default: {
      title: 'Coming Soon',
      description: 'This feature is currently under development and will be available soon.',
      icon: <Calendar size={64} className="text-gray-600" />,
      features: [
        'Feature in development',
        'Stay tuned for updates',
        'We\'re working hard to bring you the best experience'
      ]
    }
  };

  const config = moduleConfig[module] || moduleConfig.default;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Back Button */}
          <div className="flex justify-start mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              icon={<ArrowLeft size={20} />}
              className="text-gray-600 hover:text-gray-800"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            {config.icon}
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {config.title}
          </h1>

          {/* Coming Soon Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-800 font-semibold text-sm mb-6">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse"></span>
            Coming Soon
          </div>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            {config.description}
          </p>

          {/* Features */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What's Coming:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
              {config.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Explore Other Features
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              We're constantly working to improve iDecide. Follow us for updates on new features!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 